import { PRIVACY_PATH } from '../route.ts';
import type { LegalDocument } from './documents.ts';

/**
 * The privacy policy, as data.
 *
 * Every factual claim here was read from the code or the running deployment on
 * 2026-08-27, and the support-ticket and waiting-list passages on 2026-09-25. Where a fact does not exist yet — the contracting entity, a
 * retention schedule — the text carries a visible placeholder rather than a
 * plausible sentence, and the final section lists every one of them. A policy
 * whose only value is that an auditor can rely on it cannot contain a guess.
 *
 * It is drafted alongside the data processing agreement in `dpaContent.ts` and
 * must stay consistent with it: the DPA is worthless if the privacy policy
 * describes different data, different sub-processors or a different retention
 * position. The two cross-reference each other by section id.
 */
export const PRIVACY: LegalDocument = {
  id: 'privacy',
  path: PRIVACY_PATH,
  draft: true,
  title: 'Privacy policy',
  version: '0.2.5-draft',
  updated: '2026-09-25',
  summary:
    'How DriftWatch handles personal data — both the small amount we hold about visitors to this ' +
    'site, where we decide what happens to it, and the tenant configuration we read on a ' +
    'customer’s instructions, where they do. Draft for legal review.',
  sections: [
    {
      id: 'draft-status',
      heading: 'Status of this document',
      paragraphs: [
        'This is a draft for legal review. It has not been reviewed or approved by a qualified ' +
          'data protection lawyer, and it is not yet a binding statement of how Drift Watch ' +
          'handles personal data.',
        'It is published in this state deliberately. The factual content — what the product ' +
          'reads, what it refuses to read, where it runs, and who else is involved — was taken ' +
          'from the running system rather than from a template, and it is more useful to a ' +
          'customer’s reviewer in draft than it would be in six months’ time in final form. The ' +
          'parts that are not yet settled appear as square-bracketed placeholders and are listed ' +
          'in full at the end.',
        'Where this policy and the DriftWatch data processing agreement address the same subject, ' +
          'the data processing agreement governs the relationship with a customer. This policy ' +
          'explains it.',
      ],
    },
    {
      id: 'who-we-are',
      heading: 'Who we are',
      paragraphs: [
        'DriftWatch is a service operated by Drift Watch, company number [COMPANY NUMBER], ' +
          'registered at [REGISTERED ADDRESS].',
        'It reads a customer’s Microsoft 365 and Microsoft Entra tenant configuration on a ' +
          'read-only basis, evaluates it against a versioned catalogue of controls, ' +
          'and produces a report. Those controls are projected onto seven frameworks: SOC 2, ' +
          'ISO/IEC 27001:2022 Annex A, UK and EU GDPR Article 32, Cyber Essentials, the CIS ' +
          'Critical Security Controls v8, the CIS Microsoft 365 Foundations Benchmark and NIST ' +
          'CSF 2.0.',
        'DriftWatch is not a certification body and does not issue certifications against any of ' +
          'those frameworks. It reports what a tenant’s configuration does and does not do.',
        'For any question about this policy, or to exercise a right described in it, contact ' +
          '[PRIVACY CONTACT EMAIL]. Our data protection officer or representative, where one is ' +
          'appointed, is [DPO OR REPRESENTATIVE].',
      ],
    },
    {
      id: 'two-roles',
      heading: 'Two groups of people, and two different roles',
      paragraphs: [
        'This policy covers two groups of people whose relationship with us is not the same, and ' +
          'conflating them would make it useless to both.',
        'The first group is people who come to this website and, in some cases, join the waiting ' +
          'list. For them we are the controller: we decided what the waiting-list form asks for ' +
          'and we decide what happens to it. The section on the waiting list applies to you.',
        'The second group is people whose accounts, groups and role assignments exist inside a ' +
          'customer’s Microsoft 365 tenant. Most of them have never heard of DriftWatch, did not ' +
          'choose it, and cannot leave it. For them we are a processor acting on a customer’s ' +
          'instructions, and the customer — or, where the customer is a managed service ' +
          'provider, that provider’s client — is the controller. The sections on tenant data and ' +
          'on what we refuse to collect apply to you, and they are written at length precisely ' +
          'because you had no say in it.',
        'A third, smaller group is the people at our customers who sign in to the DriftWatch ' +
          'console and write to us through its Support page. For what they write to us we are ' +
          'the controller: we decide what a support conversation needs and what happens to it. ' +
          'The section on support tickets applies to you.',
        'Which of the first two applies to a given customer relationship is set out in the data ' +
          'processing agreement, section roles.',
      ],
    },
    {
      id: 'what-we-collect',
      heading: 'What we collect from visitors and waiting-list sign-ups',
      paragraphs: [
        'From the public site, we collect what somebody types into the waiting-list form when ' +
          'they choose to join it. Two fields are required: your name and a work email address. ' +
          'Three are optional: your organisation, a band for how many Microsoft 365 tenants you ' +
          'look after, and a free-text answer of up to 1,000 characters to the question of which ' +
          'framework you are working on. The email address is how we tell you when we open and ' +
          'the name is how we address you; the optional answers tell us who is asking and what ' +
          'to build first. There is no telephone field.',
        'With each sign-up we also store the IP address it came from. We use it for one thing: ' +
          'refusing further sign-ups from an address that has just made several, so that a ' +
          'script cannot fill the list. It is not shown on the screen where we read the list. ' +
          'When a sign-up is refused for that reason, the address is also written to the server ' +
          'log.',
        'Joining again with the same email address does not create a second entry. It replaces ' +
          'the name, replaces any optional answer you give again and keeps any you leave out; the ' +
          'date you first joined and the IP address you first joined from are kept.',
        'Our lawful basis for what you type into the form is consent: you gave it to us asking ' +
          'to be told when we open. You can withdraw it at any time by writing to [PRIVACY ' +
          'CONTACT EMAIL], and we will delete the record, including the IP address stored with ' +
          'it.',
        'The IP address rests on a different basis, legitimate interests: keeping the list usable ' +
          'by stopping automated sign-ups. You did not type it in, so consent would be the wrong ' +
          'word for it. You can object to it, and leaving the list deletes it.',
        'Waiting-list sign-ups are held outside the tenant isolation boundary that separates ' +
          'customer data, because somebody who has asked to be told when we open is not ' +
          'associated with any customer tenant. They can be read only by DriftWatch’s own ' +
          'platform staff, never by a customer, and every time the list is read the audit log ' +
          'records who read it and how many entries it held.',
        'There is no payment processing anywhere in DriftWatch. No card details, bank details or ' +
          'other financial data are handled at any point.',
      ],
    },
    {
      id: 'cookies',
      heading: 'Cookies and browser storage',
      paragraphs: [
        'When an operator signs in to the console, a session cookie is set. It is httpOnly, so ' +
          'the console’s own JavaScript cannot read it, and it is a reference to a session record ' +
          'held in our database rather than a token carrying account details. It is strictly ' +
          'necessary for the console to work.',
        'A full inventory of any other cookie or browser-storage item set by this site — ' +
          'including whether any analytics or measurement is in use — is [COOKIE AND ANALYTICS ' +
          'INVENTORY] and must be verified against the deployed site before this policy is ' +
          'published in final form. We have not claimed here that there are none, because that ' +
          'is a claim that should be checked rather than assumed.',
      ],
    },
    {
      id: 'support-tickets',
      heading: 'Support tickets',
      paragraphs: [
        'Anybody signed in to the console can open a support ticket. A ticket holds what you ' +
          'give us and what is needed to keep the conversation legible: a subject, whether it is ' +
          'a bug, a feature request or a suggestion, each message you and we write, at most one ' +
          'image per message, the display name of whoever wrote each message, which customer ' +
          'account it belongs to, and when each message was sent.',
        'Everybody signed in to the same customer account can read all of that account’s ' +
          'tickets and their replies, so a colleague can follow up when the person who raised ' +
          'one is away. Nobody in any other customer account can. DriftWatch’s own support staff ' +
          'can read every ticket, because answering them is what they are for.',
        'Please put in a ticket only what we need to help. Screenshots in particular can show ' +
          'far more than intended — other people’s names, email addresses, or configuration from ' +
          'a tenant you manage. If an image shows personal data that the question does not need, ' +
          'crop or blur it first. When you attach an image in the console, it is redrawn in your ' +
          'browser before it is sent, which removes information embedded in the file, such as ' +
          'the location a photograph was taken; the visible picture is sent as it is.',
        'One part of a ticket is not ours. If it contains personal data from a Microsoft 365 ' +
          'tenant your organisation assesses — a screenshot of a report that names users, for ' +
          'example — that data stays under the control of whoever controls the tenant, and we ' +
          'handle it as their processor under the data processing agreement, only to answer the ' +
          'ticket.',
        'Tickets are stored in our database in Frankfurt with the rest of the service. They are ' +
          'not sent to Anthropic or any other third party, they are not used to train any model, ' +
          'and nothing is emailed: replies appear in the console. The audit log records that a ' +
          'ticket was opened, answered or closed, but never its subject, its messages or its ' +
          'images, because the audit log is read by more people than the ticket is.',
        'Our lawful basis is legitimate interests: supporting the people who use a service their ' +
          'organisation has chosen, and fixing what they tell us is wrong. The person writing is ' +
          'usually an employee of our customer rather than a party to our contract with them, ' +
          'which is why we do not rely on contract.',
      ],
    },
    {
      id: 'tenant-data',
      heading: 'What we read from a customer’s tenant',
      paragraphs: [
        'DriftWatch reads tenant configuration, not tenant content. It does not read file ' +
          'contents, message bodies, calendar entries or SharePoint documents, and it has no ' +
          'permission that would let it.',
        'Configuration nevertheless contains personal data, and it is more honest to name it than ' +
          'to describe it as "metadata". From the configured sources listed in the source ' +
          'catalogue we read:',
        'The customer’s own staff who use the DriftWatch console also have personal data held ' +
          'here: an email address, a display name, a password stored as a scrypt hash that cannot ' +
          'be reversed, session records and records of sign-in attempts. Those accounts belong to ' +
          'the organisation running the deployment, and we hold them as processor for that ' +
          'organisation in the same way as the rest.',
      ],
      bullets: [
        'User principal names, display names and directory identifiers',
        'Last sign-in dates, used to assess dormant accounts',
        'Group names, identifiers and membership',
        'Directory role assignments, including privileged ones',
        'The directory identifier of a mailbox owner where an inbox rule forwards mail',
        'The directory identifier of the account behind each shared or Bookings mailbox, used to assess whether it can be signed in to',
        'Names that administrators give to Conditional Access policies, mail flow rules, ' +
          'Safe Links, Safe Attachments and anti-phishing policies and rules, and data loss ' +
          'prevention and sensitivity label policies, which can incidentally name a person or a ' +
          'partner',
        'Whether each account has registered a multi-factor authentication method its organisation ' +
          'allows, as two yes-or-no answers against its directory identifier',
      ],
    },
    {
      id: 'what-we-do-not-collect',
      heading: 'What we deliberately do not collect',
      paragraphs: [
        'This section is unusual in a privacy policy, and it earns its place. The people it ' +
          'protects are in the second group described above: they never chose this product. Each ' +
          'item below is data that Microsoft Graph would have handed us and that the collector ' +
          'discards or never asks for, and each is verifiable by reading the source.',
        'The mail-forwarding case is worth spelling out. When a control finds an inbox rule ' +
          'forwarding mail outside the organisation, what matters to the assessment is the ' +
          'destination domain, not the person at it. The local part of the recipient address is ' +
          'discarded in the collector, before anything is written down. The finding says mail ' +
          'leaves for a given domain; it does not say who receives it.',
      ],
      bullets: [
        'Device names, serial numbers, IMEI and MEID numbers, telephone numbers, ICCIDs and MAC ' +
          'addresses',
        'IP addresses and geographic locations from Identity Protection risk detections',
        'The email addresses of mail-forwarding recipients — only the domain survives',
        'Sensitivity-label descriptions and tooltips, which are administrator-written prose and ' +
          'routinely name internal projects and clients',
        'The expressions administrators write into Conditional Access device, application and ' +
          'workload-identity filters, which can name devices, asset tags and project attributes — ' +
          'only which of Microsoft’s documented device properties a filter tests is kept',
        'The identifiers of partner organisations a Conditional Access guest condition lists — ' +
          'only how many there are is kept',
        'These two Conditional Access items have been discarded since 21 September 2026. ' +
          'Configuration collected before then still holds them in full, until it is rewritten ' +
          'or deleted',
        'The contents of Intune configuration profiles, which would otherwise arrive as a whole ' +
          'configuration payload',
        'Per-site SharePoint sharing, sharing links, and the identities of guests',
        'Any name, address or identifier for a mailbox’s SMTP AUTH setting — only whether each ' +
          'mailbox follows, enables or disables it, with no way to tell which mailbox is which',
        'The partner domains a calendar sharing policy names, or the domains and addresses ' +
          'exempted from Outlook’s external tag — only how many there are and, for calendar ' +
          'sharing, the most detail they are allowed',
        'The names, sign-in addresses and registered authentication methods Microsoft’s MFA ' +
          'registration report offers — only whether each account is registered',
        'The people an anti-phishing policy protects from impersonation — only how many there ' +
          'are is kept — and the protected domains, excluded senders and excluded domains it ' +
          'lists, which are not kept at all',
        'Whom a Safe Links, Safe Attachments, anti-phishing or preset security policy rule ' +
          'applies to — recipient addresses, group names and other organisations’ domains are ' +
          'reduced to yes-or-no answers and counts; only the organisation’s own accepted domains ' +
          'may be kept',
        'These two mail-protection items have been discarded since 21 September 2026. ' +
          'Anti-phishing policies collected before then still hold the people and domains they ' +
          'protect in full, until they are rewritten or deleted',
        'Where a data loss prevention or sensitivity label policy applies — the sites, ' +
          'mailboxes, users, groups and teams it names, and which labels it publishes. Only ' +
          'whether it covers every location in a service, how many it names, or none is kept, ' +
          'with how many exceptions it has and how many labels it publishes',
        'What a data loss prevention rule looks for and what it does — sensitive information ' +
          'types, keywords, patterns, thresholds, actions and who is notified. Only the policy a ' +
          'rule belongs to and whether it is switched on are kept. Nor is who created or last ' +
          'changed a Purview policy, or any comment on it',
      ],
    },
    {
      id: 'read-only-access',
      heading: 'Access to a customer’s tenant is read-only, and enforced three ways',
      paragraphs: [
        'DriftWatch reads a customer’s tenant and changes nothing in it. How that is enforced ' +
          'differs between the Microsoft services we read, and both halves are set out here.',
        'For Microsoft Graph, which is most of what we read, the permissions themselves cannot ' +
          'write: twenty-one application permissions in a frozen list, every one of them a read ' +
          'scope. That is enforced three ways rather than promised once. The scope list is ' +
          'frozen. The HTTP transport refuses any method but GET, so a write request cannot be ' +
          'issued even if a permission somehow existed. And a test greps the source tree for ' +
          'write-scope strings and fails the build if one appears.',
        'Exchange Online and SharePoint tenant administration publish no read-only permission, ' +
          'so reading them requires two permissions that are capable of more: ' +
          'Exchange.ManageAsApp and Sites.FullControl.All. What confines them to reading is a ' +
          'frozen list of read-only commands, a runner that cannot be handed a command by any ' +
          'other route, and a re-reading of the generated script that refuses anything not on ' +
          'the list. For Exchange there is a fourth and stronger mechanism that belongs to the ' +
          'customer rather than to us: the account is granted only view-only administrative ' +
          'roles in their tenant, and every assessment verifies that grant before it uses ' +
          'anything it has read, discarding the run if it cannot. Because an Entra directory ' +
          'role such as Exchange Administrator would grant Exchange permissions that check does ' +
          'not see, every assessment also refuses to read Exchange if the account holds any ' +
          'directory role other than Teams Reader. SharePoint has no equivalent, ' +
          'so that path rests on our code alone.',
        'Microsoft Purview, read for data loss prevention and sensitivity label policies, is ' +
          'reached through the same Exchange permission but has roles of its own. The customer ' +
          'adds the account to a Purview role group holding three view-only roles, and it holds ' +
          'no directory role other than Teams Reader. Microsoft describes Teams Reader as able ' +
          'to read everything in the Teams admin centre, which includes usage reports as well ' +
          'as settings, and basic properties in the Microsoft 365 admin centre; it can change ' +
          'nothing. Microsoft’s view-only roles are not entirely read-only: together ' +
          'they allow six commands that are not reads (Set-DlpEdmSchema, Cancel-DlpEdmSession, ' +
          'Execute-AzureAdLabelSync, Check-PurviewConfig, Export-PurviewConfig and ' +
          'Validate-RetentionRuleQuery), and no narrower role reads data loss prevention ' +
          'policies. DriftWatch never runs any of them: they are outside its frozen list. Every ' +
          'assessment also discards what it read from Purview if the account holds any ' +
          'directory role other than Teams Reader, or if the Purview session offers any command that is not a read ' +
          'other than those six.',
        'The product publishes this list, including the residual risk of each write-capable ' +
          'permission, at its own permissions endpoint, so that what a security questionnaire ' +
          'is told and what the running system holds cannot drift apart.',
        'Adding a write capability would be a deliberate architectural decision with its own ' +
          'review, not a configuration change somebody could make by accident.',
      ],
    },
    {
      id: 'lawful-basis',
      heading: 'Lawful basis',
      paragraphs: [
        'For waiting-list sign-ups, where we are the controller, the basis for what you type ' +
          'into the form is consent, and it can be withdrawn at any time. The IP address stored ' +
          'with a sign-up is kept on the basis of legitimate interests, to stop automated ' +
          'sign-ups.',
        'For support tickets, where we are also the controller, the basis is legitimate ' +
          'interests, as described in the section on support tickets.',
        'For everything read from a customer’s tenant, we do not determine a lawful basis, ' +
          'because we are not the controller of it. The customer — or, where the customer is a ' +
          'managed service provider, that provider’s client — determines the purpose and the ' +
          'basis, and instructs us. In practice the basis a controller relies on for a security ' +
          'configuration assessment is usually legitimate interests or a legal obligation, but ' +
          'that is their determination to record and to justify, not ours to assert on their ' +
          'behalf.',
        'Our obligations in that role, including that we process only on documented instructions, ' +
          'are set out in the data processing agreement, section instructions.',
      ],
    },
    {
      id: 'ai-processing',
      heading: 'The narrative layer, and what can reach a model',
      paragraphs: [
        'DriftWatch can use a large language model to write the explanatory prose in a report — ' +
          'why a finding matters, and how to fix it. The third party is Anthropic PBC in the ' +
          'United States, and it is named as a sub-processor below. The product runs fully ' +
          'without it: with the narrative layer off, findings use static remediation text written ' +
          'in advance and nothing is sent to a model.',
        'It is switched on or off for a whole environment, not per customer: there is no ' +
          'per-customer choice in the product yet. Where it is on, it runs whenever an environment ' +
          'holds an Anthropic API key and has not been told to switch it off. The position in our ' +
          'two environments on 22 September 2026:',
        'Staging: on. Staging assesses one real customer tenant, connected through that ' +
          'customer’s own consent, so redacted findings about that tenant are sent to Anthropic.',
        'Production: off since 21 September 2026. It was meant to be off before then and was not. ' +
          'A switch written as "true" was read by code that recognised only "1", so on 19 ' +
          'September 2026 production sent Anthropic redacted findings about the one customer ' +
          'tenant it had connected, in forty-five requests. The switch has been corrected. A stricter ' +
          'reading of every switch, which refuses to start on a value it does not recognise ' +
          'rather than guessing, reaches production with its next release. Production stays off ' +
          'until the transfer safeguards described under international transfers are in place.',
        'The model is asked only about findings the rules engine has already decided: failures, ' +
          'and some findings it could not assess. A pass is never sent, and nor is a finding ' +
          'whose text is already in our cache. What is sent for each is the control’s own ' +
          'catalogue text, the verdict, the thresholds the engine applied, and the redacted ' +
          'evidence described next.',
        'Everything that can reach it passes through a single point in the code, and only that ' +
          'point may construct an outbound prompt. Each piece of evidence is classified at the ' +
          'moment it is created — as a person, a group, a service principal, a policy, a domain, ' +
          'a tenant, free text written by an administrator, or as configuration such as a count ' +
          'or a threshold. Anything in the first seven categories is replaced with a stable ' +
          'pseudonym: USER_001, GROUP_003, POLICY_002. Only counts, thresholds, enumerated values ' +
          'and the names of Microsoft’s own built-in roles pass through unchanged, because they ' +
          'are the same in every tenant and carry no information about a customer. A custom role ' +
          'is named by an administrator, so it is pseudonymised like any other free text.',
        'The result is then scanned before it is allowed out. If anything resembling an email ' +
          'address, an external guest identifier, an object identifier that is not one of ' +
          'Microsoft’s published constants, or any original value that should have been ' +
          'pseudonymised is still present, the scan raises an error and the scan fails. It fails ' +
          'closed: a mis-classification the scanner recognises breaks the assessment rather than ' +
          'becoming a disclosure. The refusal records only what kind of value was found, which ' +
          'control and which evidence item — never the value itself. Until 22 September 2026 it ' +
          'also wrote the blocked value into the scan record, the job queue and our logs; it no ' +
          'longer does.',
        'The scanner is a check on the classification, not a replacement for it. A person’s name ' +
          'wrongly classified as configuration, and appearing nowhere else in the same finding, ' +
          'is not something it would recognise. That is why every piece of evidence is classified ' +
          'where it is created, and why a test runs every control over our synthetic test tenants ' +
          'through the same chokepoint.',
        'The text that comes back is stored in pseudonymised form. Real names are substituted ' +
          'only at the moment a report is rendered. That is also why generated text can be cached ' +
          'and reused across customers without risk — what is cached contains no identifiers at ' +
          'all, only tokens, and each customer’s own names are put back at render time from a ' +
          'mapping that never leaves our infrastructure.',
        'Anthropic’s published terms for its API say that it may not train models on customer ' +
          'content (Commercial Terms of Service, effective 17 June 2025), and its published ' +
          'retention policy is to delete API inputs and outputs within 30 days, longer only where ' +
          'it needs them to enforce its usage policy or the law requires it (Anthropic Privacy ' +
          'Center, updated 1 July 2026). Those are Anthropic’s published commitments. We rely on ' +
          'them as published and have no separately negotiated agreement with Anthropic.',
      ],
    },
    {
      id: 'automated-decisions',
      heading: 'No automated decision-making about individuals',
      paragraphs: [
        'A reader who sees that a language model is involved will reasonably assume it decides ' +
          'something. It does not, and this is worth stating plainly.',
        'Every verdict in a DriftWatch report is produced by a deterministic rules engine: a pure, ' +
          'unit-tested function over a normalised model of the tenant configuration, which ' +
          'returns a pass, a fail, a not-applicable or an insufficient-data result together with ' +
          'the evidence that produced it. The same configuration always produces the same ' +
          'verdict. The model writes prose about a decision that has already been made and cannot ' +
          'influence it; no prompt asks whether something is compliant. If the prose and the ' +
          'verdict ever disagree, the verdict is right and the prose is a defect.',
        'There is therefore no automated decision-making, and no profiling, producing legal or ' +
          'similarly significant effects concerning any individual within the meaning of Article ' +
          '22. Findings describe the configuration of a tenant. Where a finding names an account ' +
          '— a privileged role held without multi-factor authentication, for instance — it is a ' +
          'statement about a configuration, not an evaluation of a person, and any action taken ' +
          'in response is taken by the customer’s administrators exercising their own judgement.',
        'The same statement appears in the data processing agreement, section automated-decisions.',
      ],
    },
    {
      id: 'subprocessors',
      heading: 'Who else is involved',
      paragraphs: [
        'We use the following sub-processors. The authoritative list, and the notice we give ' +
          'before changing it, is in the data processing agreement, section annex-subprocessors.',
        'Support tickets reach no sub-processor other than IONOS SE, which hosts them with the ' +
          'rest of the service. They are not sent to Anthropic PBC, and there is no email or ' +
          'ticketing service involved.',
        'Microsoft is not a sub-processor of ours, and a reader will ask, so: the Microsoft 365 ' +
          'tenant being assessed is the customer’s own service under the customer’s own agreement ' +
          'with Microsoft. It is the data source, not somewhere we send data. We read from it and ' +
          'write nothing to it.',
      ],
      bullets: [
        'IONOS SE — hosting, database and compute, in Frankfurt am Main, Germany. Always in use.',
        'Anthropic PBC — generation of narrative text from redacted, pseudonymised findings, in ' +
          'the United States. The data is configuration findings with every customer-specific ' +
          'identifier replaced by a token, designed to carry no name, email address or object ' +
          'identifier of any person. In use in ' +
          'staging, which assesses one real customer tenant; switched off in production since 21 ' +
          'September 2026, after it was used there on 19 September 2026. See the narrative layer ' +
          'section above.',
        'Let’s Encrypt, operated by the Internet Security Research Group — TLS certificates. No ' +
          'customer data reaches it.',
      ],
    },
    {
      id: 'transfers',
      heading: 'Where data is held, and international transfers',
      paragraphs: [
        'DriftWatch runs on a single host in Frankfurt am Main, Germany, operated by IONOS SE. ' +
          'The PostgreSQL database and the Redis job queue are on that host. Hosting is therefore ' +
          'within the EEA and requires no transfer mechanism of its own.',
        'Traffic to the site and to the API is served over TLS, using certificates issued by ' +
          'Let’s Encrypt and renewed automatically.',
        'The one transfer outside the EEA is the narrative layer, which sends redacted findings ' +
          'to Anthropic PBC in the United States. It is in use in staging, which assesses one ' +
          'real customer tenant, and it was used in production on 19 September 2026; production ' +
          'has had it switched off since 21 September 2026. What is sent has been pseudonymised ' +
          'and leak-scanned as described above.',
        'What is not yet in place, stated plainly. Anthropic’s published Commercial Terms, ' +
          'which govern use of its API, incorporate its Data Processing Addendum, and that ' +
          'addendum contains the EU Standard Contractual Clauses (modules two and three) and the ' +
          'UK International Data Transfer Addendum. We have not yet recorded that as the ' +
          'mechanism we rely on, and we have not yet carried out or documented the transfer risk ' +
          'assessment that has to accompany it. The staging use has been running without either. ' +
          'Production stays switched off until both are in place. The mechanism is shown as ' +
          '[TRANSFER MECHANISM] until then, and the same position is recorded in the data ' +
          'processing agreement, section transfers.',
      ],
    },
    {
      id: 'security',
      heading: 'How it is protected',
      paragraphs: [
        'The measures below are the ones actually implemented, and each is verifiable in the ' +
          'source. The full description an auditor would want is in the data processing ' +
          'agreement, section annex-security-measures.',
      ],
      bullets: [
        'Tenant isolation enforced twice: every table holding tenant data carries a tenant ' +
          'identifier, every repository call runs inside an explicit tenant context, and a ' +
          'PostgreSQL row-level-security policy enforces the same boundary again at the database. ' +
          'A schema-level test fails by name any table that has a tenant identifier and no ' +
          'isolation policy, so the mechanism does not depend on anyone remembering to add to a ' +
          'list.',
        'Envelope encryption for stored tenant credentials, wrapped by a master key that is ' +
          'unique to each environment and held only on that host.',
        'Operator passwords stored as scrypt hashes, which cannot be reversed. Sessions are held ' +
          'in the database and referenced by an httpOnly cookie the console cannot read.',
        'Capability-based authorisation, denying by default: an operator can do only what a ' +
          'capability explicitly grants.',
        'An audit log of operator actions.',
        'Support tickets isolated by customer account: every ticket read is bound to the account ' +
          'of the person asking, another account’s ticket is answered as not found, and ' +
          'attachments are accepted only as PNG, JPEG or WebP images whose contents are checked, ' +
          'never as SVG or any other format.',
        'Read-only access to customer tenants, enforced three ways as described above.',
        'A single redaction chokepoint, failing closed, as the only path to a third-party model.',
      ],
    },
    {
      id: 'retention',
      heading: 'How long we keep it',
      paragraphs: [
        'The accurate answer today is that there is no automatic retention or deletion schedule ' +
          'for scans, snapshots or findings. Nothing ages out on its own.',
        'What does happen automatically is narrow: expired sessions are purged. On request, ' +
          'purging a tenant cascades and removes its collected evidence with it.',
        'We are stating this rather than writing a period we do not enforce, because a retention ' +
          'claim that the system does not implement is exactly the kind of overstatement this ' +
          'product exists to find in other people’s environments. It is a gap, it is recorded as ' +
          'one, and the schedule that will close it is [RETENTION SCHEDULE].',
        'Until then, deletion is available on request under the data processing agreement, ' +
          'section deletion, and we will act on such a request rather than waiting for a policy ' +
          'to expire the data for us.',
        'Waiting-list entries are not deleted automatically either. Each one, with the IP ' +
          'address stored beside it, is kept until you ask us to remove it; the schedule that ' +
          'will close the gap above is meant to cover them too.',
        'Support tickets are kept until the customer account they belong to is closed and ' +
          'deleted, which deletes them with it, or until deletion is requested. If the person who ' +
          'wrote a message later leaves their organisation’s account, their message and their ' +
          'display name stay with the ticket, so that the conversation still makes sense; ask us ' +
          'and we will remove or anonymise them.',
        'Backups: databases are dumped nightly and those dumps are kept for seven days, on the ' +
          'same host. There is no off-site copy. This should not be described as disaster ' +
          'recovery, and we are not describing it as such.',
      ],
    },
    {
      id: 'your-rights',
      heading: 'Your rights, and how to exercise them',
      paragraphs: [
        'If you are on the waiting list, we are the controller and you can exercise your rights ' +
          'directly with us: access to what we hold, rectification, erasure, restriction, ' +
          'portability, objection, and withdrawal of consent at any time. Write to [PRIVACY ' +
          'CONTACT EMAIL]. What we hold is the name, email address and any optional answers you ' +
          'gave us, and the IP address your sign-up came from, so in practice most of these ' +
          'resolve to telling you what they are or deleting them.',
        'If your data reached us from a Microsoft 365 tenant, we are a processor and the right ' +
          'route is your own organisation, or the organisation whose tenant it is. They are the ' +
          'controller; they hold the relationship and the context; and they can act where we ' +
          'cannot. If you write to us instead we will not ignore you — we will tell you which ' +
          'controller holds the data and, unless instructed otherwise, tell them you have been in ' +
          'touch, so that a request is not lost between two organisations.',
        'If you have written to us through the Support page, we are the controller of what you ' +
          'wrote and you can exercise the same rights directly with us at [PRIVACY CONTACT ' +
          'EMAIL]. You can also object to our legitimate-interests processing; because a ticket ' +
          'is shared with your colleagues, we may need to tell your organisation’s account owner ' +
          'before deleting one that others are relying on.',
        'Our obligation to assist a controller in responding to such requests, and to do so ' +
          'promptly, is in the data processing agreement, section data-subject-requests.',
      ],
    },
    {
      id: 'complaints',
      heading: 'Complaints',
      paragraphs: [
        'If you are unhappy with how we have handled your personal data, please tell us first at ' +
          '[PRIVACY CONTACT EMAIL] so that we have a chance to put it right.',
        'You also have the right to complain to a supervisory authority without coming to us ' +
          'first. Ours is [SUPERVISORY AUTHORITY]. You may instead complain to the supervisory ' +
          'authority in the country where you live or work, or where you believe the problem ' +
          'occurred.',
      ],
    },
    {
      id: 'what-we-do-not-claim',
      heading: 'What this policy does not claim',
      paragraphs: [
        'DriftWatch assesses other organisations against SOC 2, ISO/IEC 27001 and other ' +
          'frameworks. It does not hold them itself, and saying otherwise would be precisely the ' +
          'overstatement the product sells itself on catching. So, plainly:',
        'A description that overstates itself is worse than one that admits a gap. The gap is a ' +
          'finding; the overstatement is a misrepresentation. We would rather a customer’s auditor ' +
          'found this section boring and accurate than impressive and wrong.',
      ],
      bullets: [
        'There is no SOC 2 report for DriftWatch.',
        'There is no ISO/IEC 27001 certification for DriftWatch.',
        'There has been no penetration test of DriftWatch.',
        'There is no service level agreement.',
        'There are no off-site backups. Nightly dumps are kept for seven days on the same host.',
        'Code review is single-maintainer development with automated review and enforced ' +
          'continuous-integration gates. It is not peer review, and we will not describe it as ' +
          'such in any questionnaire, narrative or submission.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to this policy',
      paragraphs: [
        'This document carries a version and a date, both of which are fields in the source ' +
          'rather than sentences in a paragraph, so that the edit which changes the wording is ' +
          'the same edit that changes the date.',
        'This version is 0.2.5-draft, dated 25 September 2026. Version 0.2.0-draft added ' +
          'support tickets; 0.2.1-draft and 0.2.2-draft corrected what the waiting list ' +
          'collects, on what basis, who can read it and how long it is kept; 0.2.3-draft said ' +
          'that tenant data inside a ticket stays the tenant controller’s, as the data ' +
          'processing agreement 0.2.0-draft does; and this one adds support tickets to the ' +
          'section on who else is involved, matching the terms of service 0.2.0-draft; 0.2.5-draft ' +
          'names the operator, Drift Watch, in place of a placeholder. When it moves out of draft, and ' +
          'whenever a later change materially affects how we handle personal data, we will update ' +
          'the version and the date here. Customers under a data processing agreement are ' +
          'notified of changes to sub-processors under the terms of that agreement rather than by ' +
          'a change to this page.',
      ],
    },
    {
      id: 'contact',
      heading: 'Contact',
      paragraphs: [
        'Drift Watch, company number [COMPANY NUMBER], [REGISTERED ADDRESS].',
        'Privacy enquiries and rights requests: [PRIVACY CONTACT EMAIL].',
        'Data protection officer or representative: [DPO OR REPRESENTATIVE].',
        'Supervisory authority: [SUPERVISORY AUTHORITY]. Governing law: [GOVERNING LAW].',
      ],
    },
    {
      id: 'placeholders',
      heading: 'Placeholders in this draft',
      paragraphs: [
        'Every square-bracketed placeholder in this document is listed here, so that nobody has ' +
          'to search the text to find out what is still unresolved. This document must not be ' +
          'published as final while any of them remain.',
      ],
      bullets: [
        '[COMPANY NUMBER] — Drift Watch’s company registration number.',
        '[REGISTERED ADDRESS] — Drift Watch’s registered office.',
        '[GOVERNING LAW] — the law governing this policy and the related agreements.',
        '[PRIVACY CONTACT EMAIL] — the address for privacy enquiries and rights requests.',
        '[SUPERVISORY AUTHORITY] — our lead supervisory authority, which follows from where the ' +
          'entity is established.',
        '[DPO OR REPRESENTATIVE] — a data protection officer or Article 27 representative, if one ' +
          'is required or appointed.',
        '[COOKIE AND ANALYTICS INVENTORY] — the verified list of cookies and browser storage used ' +
          'by the public site, which must be checked against the deployed site rather than ' +
          'assumed.',
        '[TRANSFER MECHANISM] — the mechanism relied on for the transfer to Anthropic PBC in the ' +
          'United States, which staging already makes, together with the transfer risk ' +
          'assessment that must accompany it. Neither is in place yet.',
        '[RETENTION SCHEDULE] — the retention and deletion schedule that does not exist yet, and ' +
          'whose absence is recorded as a gap in the retention section above.',
      ],
    },
  ],
};
